import { Badge, Group, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconBriefcase, IconBuilding, IconCalendar, IconMapPin, IconUserCheck } from "@tabler/icons-react";
import { JobAdditionalDetails } from "../../components/jobs/JobAdditionalDetails";
import { JobFactCard } from "../../components/jobs/JobFactCard";
import { JobHiringFlow } from "../../components/jobs/JobHiringFlow";
import { JobInsightAlerts } from "../../components/jobs/JobInsightsBanner";
import { ContentCard } from "../../components/ui";
import { formatCities, formatDate, formatJobExperienceRange, formatPaise, SEGMENT_LABELS } from "../../lib/format";
import { axioTokens } from "../../theme";
import type { AdminJob, JobInsight } from "../../types/api";

const PREVIEW_COPY = {
  description:
    "Verified recruiter job page — title, facts, skills, JD, hiring flow, and published insights. Approx earnings are omitted.",
  emptyInsights: "No published insights on the recruiter job page yet.",
  emptyInsightsHint: "Publish an insight from Admin to show it as a banner.",
  ctc: "CTC band",
  experience: "Experience",
  guarantee: "Guarantee window",
  jobDescription: "Job Description",
  requirements: "Requirements",
  postedPrefix: "Posted",
  remoteOk: "Remote OK",
  openingsSuffix: "openings left",
  ofLabel: "of",
  daysSuffix: "days",
} as const;

const WHITE_SPACE_PRE_WRAP = "pre-wrap" as const;
const META_ICON_SIZE = "var(--axio-unit-16)";
const META_ICON_GAP = "var(--axio-unit-4)";
const META_GROUP_GAP = "var(--axio-unit-16)";
const EMPTY_FACT = "—";
const CITY_REMOTE_SEPARATOR = " · ";

const PREVIEW_FACT_COLS = {
  base: 1,
  [axioTokens.containerQuery.sm]: 2,
} as const;

const PREVIEW_FACT_COLS_WITH_EXPERIENCE = {
  base: 1,
  [axioTokens.containerQuery.sm]: 3,
} as const;

function openingsLeftLabel(job: AdminJob): string {
  const left = job.openings_total - job.openings_filled;
  return `${left} ${PREVIEW_COPY.ofLabel} ${job.openings_total} ${PREVIEW_COPY.openingsSuffix}`;
}

function citiesMeta(job: AdminJob): string {
  const cities = formatCities(job.cities);
  return job.is_remote ? `${cities}${CITY_REMOTE_SEPARATOR}${PREVIEW_COPY.remoteOk}` : cities;
}

function ctcBand(job: AdminJob): string {
  if (job.ctc_min_paise == null && job.ctc_max_paise == null) return EMPTY_FACT;
  return `${formatPaise(job.ctc_min_paise)} – ${formatPaise(job.ctc_max_paise)}`;
}

function guaranteeLabel(job: AdminJob): string {
  return job.guarantee_days != null ? `${job.guarantee_days} ${PREVIEW_COPY.daysSuffix}` : EMPTY_FACT;
}

export function AdminJobRecruiterPreview({
  job,
  publishedInsights,
}: {
  job: AdminJob;
  publishedInsights: JobInsight[];
}) {
  const experience = formatJobExperienceRange(job.experience_min, job.experience_max);

  return (
    <Stack gap={axioTokens.unit[32]}>
      <Text size="sm" c="dimmed">
        {PREVIEW_COPY.description}
      </Text>
      <ContentCard>
        <Stack gap="md">
          <Group gap="xs">
            <Badge color="gray" variant="outline" size="sm">
              {SEGMENT_LABELS[job.segment] ?? job.segment}
            </Badge>
            <Badge color={job.status === "open" ? "green" : "gray"} variant="outline" size="sm" tt="capitalize">
              {job.status}
            </Badge>
          </Group>
          <Title order={3}>{job.title}</Title>
          <Group gap={META_GROUP_GAP} c="dimmed" wrap="wrap">
            <Group gap={META_ICON_GAP}>
              <IconBuilding size={META_ICON_SIZE} />
              <Text size="sm">{job.company_name}</Text>
            </Group>
            <Group gap={META_ICON_GAP}>
              <IconCalendar size={META_ICON_SIZE} />
              <Text size="sm" className="tabular">
                {PREVIEW_COPY.postedPrefix} {formatDate(job.published_at ?? job.created_at)}
              </Text>
            </Group>
            <Group gap={META_ICON_GAP}>
              <IconMapPin size={META_ICON_SIZE} />
              <Text size="sm">{citiesMeta(job)}</Text>
            </Group>
            <Group gap={META_ICON_GAP}>
              <IconBriefcase size={META_ICON_SIZE} />
              <Text size="sm">{openingsLeftLabel(job)}</Text>
            </Group>
          </Group>
        </Stack>
      </ContentCard>

      {publishedInsights.length > 0 ? (
        <JobInsightAlerts insights={publishedInsights} />
      ) : (
        <Text size="sm" c="dimmed">
          {PREVIEW_COPY.emptyInsights} {PREVIEW_COPY.emptyInsightsHint}
        </Text>
      )}

      <SimpleGrid type="container" cols={experience ? PREVIEW_FACT_COLS_WITH_EXPERIENCE : PREVIEW_FACT_COLS} spacing="lg">
        <JobFactCard label={PREVIEW_COPY.ctc} tabular>
          {ctcBand(job)}
        </JobFactCard>
        {experience ? (
          <JobFactCard label={PREVIEW_COPY.experience}>
            <Group gap={META_ICON_GAP}>
              <IconUserCheck size={META_ICON_SIZE} color="gray" />
              <Text fw="var(--axio-font-weight-semibold)">{experience}</Text>
            </Group>
          </JobFactCard>
        ) : null}
        <JobFactCard label={PREVIEW_COPY.guarantee} tabular>
          {guaranteeLabel(job)}
        </JobFactCard>
      </SimpleGrid>

      <JobAdditionalDetails job={job} audience="recruiter" />

      {job.description ? (
        <ContentCard>
          <Title order={5} mb="xs">
            {PREVIEW_COPY.jobDescription}
          </Title>
          <Text style={{ whiteSpace: WHITE_SPACE_PRE_WRAP }}>{job.description}</Text>
        </ContentCard>
      ) : null}

      {job.requirements ? (
        <ContentCard>
          <Title order={5} mb="xs">
            {PREVIEW_COPY.requirements}
          </Title>
          <Text style={{ whiteSpace: WHITE_SPACE_PRE_WRAP }}>{job.requirements}</Text>
        </ContentCard>
      ) : null}

      <JobHiringFlow stages={job.stages} />
    </Stack>
  );
}
